<script setup lang="ts">
import type { MaybeElementRef } from '@vueuse/core'
import BottomSheet from '@ui/components/bottom-sheet/BottomSheet.vue'
import { Placements } from '@ui/utils/placements'
import { DVH } from '@ui/utils/supportsCSS'
import Dropdown from './Dropdown.vue'
import { useAdaptiveWidth } from './useAdaptiveWidth'

type SnapPoint = number | `${number}%`

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    reference?: MaybeElementRef
    ignore?: MaybeElementRef[]
    angle?: boolean
    offset?: boolean | number
    padding?: boolean
    placements?: Placements[]
    maxHeight?: string
    shift?: boolean
    /** Width of the desktop dropdown. BottomSheet uses its own width. */
    width?: string
    label?: string
    description?: string
    closable?: boolean
    divider?: boolean
    snapPoints?: ReadonlyArray<SnapPoint>
    pending?: boolean
    mobileBreakpoint?: number
  }>(),
  {
    reference: null,
    ignore: () => [],
    angle: false,
    offset: false,
    padding: true,
    placements: () => [Placements.Top, Placements.Bottom],
    maxHeight: `calc(50${DVH} - 16px)`,
    label: '',
    description: '',
    closable: true,
    divider: false,
    // snapPoints: () => ['80%'],
    pending: false,
    mobileBreakpoint: 768,
  },
)

defineSlots<{
  default?: (props: { close: () => void; isMobile: boolean }) => void
  actions?: (props: { close: () => void }) => void
}>()

const attrs = useAttrs()
const show = defineModel<boolean>('show', { default: false })
const placement = defineModel<Placements>('placement', { default: Placements.Bottom })

const adaptiveWidth = useAdaptiveWidth()
const isMobile = computed(() => adaptiveWidth.value < props.mobileBreakpoint)

defineExpose({ isMobile })

function close() {
  show.value = false
}
</script>

<template lang="pug">
BottomSheet(
  v-if="isMobile"
  v-bind="attrs"
  v-model:show="show"
  v-model:placement="placement"
  :label="label"
  :description="description"
  :closable="closable"
  :divider="divider"
  :snapPoints="snapPoints"
  :pending="pending"
)
  slot(:close="close" :isMobile="true")
  template(v-if="$slots.actions" #actions)
    slot(name="actions" :close="close")

Dropdown(
  v-else
  v-bind="attrs"
  v-model:show="show"
  v-model:placement="placement"
  :style="width ? { width } : undefined"
  :reference="reference"
  :ignore="ignore"
  :angle="angle"
  :offset="offset"
  :padding="padding"
  :placements="placements"
  :maxHeight="maxHeight"
  :shift="shift"
)
  slot(:close="close" :isMobile="false")
</template>
